import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-areas',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NavbarComponent],
  templateUrl: './admin-areas.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminAreasComponent implements OnInit {
  @Input() embedded = false;
  sessionId = '';
  content: any[] = [];
  subjects: { id: number; name: string; courseId?: number }[] = [];
  topics: any[] = [];
  seeds: { id: number; name: string }[] = [];
  selectedSeedId: number | null = null;
  filterSubjectId: number | null = null;
  filterTopicId: number | null = null;
  loading = false;
  error = '';
  message = '';
  detailItem: any = null;
  detailForm: any = {};
  detailSaving = false;
  deleteItem: any = null;
  deleteError = '';
  sortColumn = 'name';
  sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  async ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    await this.loadSeeds();
    const defaultSeed = await this.adminRest.getAdminSeedDefault(this.sessionId);
    if (defaultSeed?.id != null) {
      this.selectedSeedId = defaultSeed.id;
    } else if (this.seeds.length > 0) {
      this.selectedSeedId = this.seeds[0].id;
    }
    await this.loadSubjects();
    await this.load();
  }

  async loadSubjects() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminSubjects(this.sessionId, undefined, this.selectedSeedId ?? undefined);
    this.subjects = list ?? [];
    if (this.subjects.length > 0 && this.filterSubjectId == null) {
      this.filterSubjectId = this.subjects[0].id;
    }
  }

  async onSubjectChange(subjectId: number | null) {
    this.filterSubjectId = subjectId ?? null;
    this.topics = [];
    this.filterTopicId = null;
    if (subjectId != null && this.sessionId) {
      const list = await this.adminRest.getAdminTopics(this.sessionId, subjectId, this.selectedSeedId ?? undefined);
      this.topics = list ?? [];
      if (this.topics.length > 0) {
        this.filterTopicId = this.topics[0].id;
      }
    }
    this.load();
  }

  async loadTopics() {
    if (!this.sessionId || this.filterSubjectId == null) return;
    const list = await this.adminRest.getAdminTopics(this.sessionId, this.filterSubjectId, this.selectedSeedId ?? undefined);
    this.topics = list ?? [];
  }

  async load() {
    if (!this.sessionId) return;
    if (this.filterSubjectId != null && this.topics.length === 0) {
      await this.loadTopics();
    }
    if (this.filterTopicId == null && this.topics.length > 0) {
      this.filterTopicId = this.topics[0].id;
    }
    // Only load areas when a topic is selected; otherwise show empty (avoids showing all areas when subject has no topics)
    if (this.filterTopicId == null) {
      this.content = [];
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      const list = await this.adminRest.getAdminAreas(this.sessionId, this.filterTopicId, this.selectedSeedId ?? undefined);
      this.content = list ?? [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load areas';
    } finally {
      this.loading = false;
    }
  }

  async loadSeeds() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminSeeds(this.sessionId);
    this.seeds = list ?? [];
  }

  async onSeedChange() {
    await this.loadSubjects();
    this.filterSubjectId = this.subjects.length > 0 ? this.subjects[0].id : null;
    this.filterTopicId = null;
    this.topics = [];
    await this.load();
  }

  onFilterChange() {
    this.load();
  }

  openCreate() {
    this.detailItem = { id: null };
    this.detailForm = { topicId: this.filterTopicId ?? this.topics[0]?.id ?? null, name: '', seedId: this.selectedSeedId };
  }

  openEdit(a: any) {
    this.detailItem = a;
    this.detailForm = { topicId: a.topicId, name: a.name ?? '', seedId: a.seedId ?? null };
  }

  closeDetail() {
    this.detailItem = null;
    this.load();
  }

  async save() {
    if (!this.sessionId) return;
    this.detailSaving = true;
    this.error = '';
    try {
      if (this.detailItem?.id == null) {
        if (!this.detailForm.name?.trim() || !this.detailForm.topicId) {
          this.error = 'Topic and name are required';
          this.detailSaving = false;
          return;
        }
        if (!this.detailForm.seedId) {
          this.error = 'Seed is required';
          this.detailSaving = false;
          return;
        }
        await this.adminRest.postAdminArea(this.sessionId, {
          topicId: this.detailForm.topicId,
          name: this.detailForm.name.trim(),
          seedId: this.detailForm.seedId
        });
        this.message = 'Area created.';
      } else {
        await this.adminRest.putAdminArea(this.sessionId, this.detailItem.id, {
          topicId: this.detailForm.topicId,
          name: this.detailForm.name,
          seedId: this.detailForm.seedId
        });
        this.message = 'Area updated.';
      }
      this.closeDetail();
    } catch (e: any) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.detailSaving = false;
    }
  }

  openDeleteConfirm(a: any) {
    this.deleteItem = a;
    this.deleteError = '';
  }

  closeDeleteConfirm() {
    this.deleteItem = null;
    this.deleteError = '';
  }

  async confirmDelete() {
    if (!this.deleteItem?.id || !this.sessionId) return;
    this.deleteError = '';
    try {
      await this.adminRest.deleteAdminArea(this.sessionId, this.deleteItem.id);
      this.closeDeleteConfirm();
      this.load();
    } catch (e: any) {
      this.deleteError = e?.error?.error || e?.message || 'Delete failed';
    }
  }

  topicName(id: number) {
    return this.topics.find(t => t.id === id)?.name ?? id;
  }

  get sortedContent(): any[] {
    const col = this.sortColumn;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.content].sort((a, b) => {
      let va: any = a[col];
      let vb: any = b[col];
      if (col === 'name') {
        va = (va ?? '') + '';
        vb = (vb ?? '') + '';
        return dir * (va.localeCompare(vb, undefined, { sensitivity: 'base' }));
      }
      if (va == null) va = '';
      if (vb == null) vb = '';
      return dir * (va < vb ? -1 : va > vb ? 1 : 0);
    });
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }
}
